import axios from "axios";

// Helper function to convert base64 VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Wait for the service worker registration to become active
function waitForServiceWorkerActive(registration) {
  return new Promise((resolve) => {
    if (registration.active) {
      return resolve(registration);
    }
    const sw = registration.installing || registration.waiting;
    if (!sw) return resolve(registration);
    sw.addEventListener("statechange", function handler() {
      if (sw.state === "activated") {
        sw.removeEventListener("statechange", handler);
        resolve(registration);
      }
    });
  });
}

/**
 * Registers the service worker only (no push subscription).
 * Safe to call before login.
 */
export const registerServiceWorker = async () => {
  if (!("serviceWorker" in navigator)) {
    console.warn("Service Worker not supported in this browser");
    return null;
  }
  try {
    const registration = await navigator.serviceWorker.register("/service-worker.js", {
      scope: "/"
    });
    console.log("Service Worker registered with scope:", registration.scope);
    return registration;
  } catch (err) {
    console.error("Service Worker registration failed:", err);
    return null;
  }
};

/**
 * Requests notification permission and subscribes to push with the current
 * auth token. Call this AFTER login so the token is in localStorage.
 */
export const subscribeToPush = async () => {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    console.warn("Push Notifications not supported in this browser");
    return;
  }

  const token = localStorage.getItem("portalToken");
  if (!token) {
    console.log("No auth token found — skipping push subscription");
    return;
  }

  try {
    // Get (or register) the service worker
    let registration = await navigator.serviceWorker.getRegistration("/");
    if (!registration) {
      registration = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });
    }

    // Wait for it to be fully active (crucial on first install)
    await waitForServiceWorkerActive(registration);

    // Request permission
    if (Notification.permission === "default") {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        console.warn("Notification permission denied");
        return;
      }
    }
    if (Notification.permission !== "granted") {
      console.log("Notifications not permitted");
      return;
    }

    // Fetch VAPID public key
    const apiURL = window.API_URL || "http://localhost:8000";
    const res = await axios.get(`${apiURL}/api/portal/push/vapid-public-key`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    const vapidPublicKey = res.data.publicKey;
    if (!vapidPublicKey) {
      console.error("VAPID public key not returned by backend");
      return;
    }

    // Subscribe (or reuse existing subscription if key matches)
    const convertedKey = urlBase64ToUint8Array(vapidPublicKey);
    let subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      let keyMatches = false;
      if (subscription.options && subscription.options.applicationServerKey) {
        const existingKey = new Uint8Array(subscription.options.applicationServerKey);
        if (existingKey.length === convertedKey.length) {
          keyMatches = existingKey.every((byte, idx) => byte === convertedKey[idx]);
        }
      }

      if (!keyMatches) {
        console.log("Existing Push Subscription uses a different VAPID key. Renewing subscription...");
        try {
          await subscription.unsubscribe();
        } catch (unsubErr) {
          console.warn("Could not cleanly unsubscribe old push subscription:", unsubErr);
        }
        subscription = null;
      }
    }

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedKey
      });
      console.log("New Push Subscription created");
    } else {
      console.log("Reusing valid Push Subscription");
    }

    // Register subscription with backend
    await axios.post(
      `${apiURL}/api/portal/push/subscribe`,
      { subscription },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    console.log("Push subscription saved to backend");
  } catch (error) {
    console.error("Error subscribing to push notifications:", error);
  }
};

/**
 * Legacy export — registers SW and subscribes in one call.
 * Used by Login.jsx after a successful login.
 */
export const registerServiceWorkerAndSubscribe = async () => {
  await registerServiceWorker();
  await subscribeToPush();
};

